import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', icon: '🍽️' });
  const [draggedId, setDraggedId] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/menu/SUC-001');
      if (res.data.status) {
        // Traemos también el conteo de productos por categoría, para avisar antes de borrar
        const cats = (res.data.data.categories || []).map(c => ({
          ...c,
          productCount: (c.products || []).length,
        }));
        setCategories(cats);
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg ? `Error del servidor: ${msg}` : 'No se pudieron cargar las categorías.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', icon: '🍽️' });
    setEditingCategory(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, form);
      } else {
        await api.post('/categories', { ...form, branch_id: 1 });
      }
      setShowForm(false);
      resetForm();
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al guardar la categoría');
    }
  };

  const startEdit = (cat) => {
    setEditingCategory(cat);
    setForm({ name: cat.name || '', description: cat.description || '', icon: cat.icon || '🍽️' });
    setShowForm(true);
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.name}"?`)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar la categoría');
    }
  };

  const handleDrop = async (targetCat) => {
    if (draggedId === null || draggedId === targetCat.id) return;

    const fromIndex = categories.findIndex(c => c.id === draggedId);
    const toIndex = categories.findIndex(c => c.id === targetCat.id);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...categories];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    setCategories(reordered);
    setDraggedId(null);

    try {
      await api.post('/categories/reorder', {
        items: reordered.map((c, idx) => ({ id: c.id, sort_order: idx })),
      });
    } catch (err) {
      alert('Error al guardar el nuevo orden');
      fetchCategories();
    }
  };

  if (loading) return <p style={{ color: '#94a3b8', textAlign: 'center', padding: 50 }}>Cargando categorías...</p>;

  if (error) return (
    <div>
      <h1 style={{ color: '#e2e8f0', fontSize: 28, marginBottom: 24 }}>🗂️ Categorías</h1>
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <p style={{ color: '#ef4444', marginBottom: 16 }}>{error}</p>
        <button onClick={fetchCategories} className="btn btn-primary">Reintentar</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ color: '#e2e8f0', fontSize: 28 }}>🗂️ Categorías</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="btn btn-primary">
          {showForm ? 'Cancelar' : '+ Nueva Categoría'}
        </button>
      </div>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        Arrastra las tarjetas para cambiar el orden en que aparecen en el menú.
      </p>

      {showForm && (
        <div className="card" style={{ marginBottom: 24, maxWidth: 480 }}>
          <h3 style={{ color: '#e2e8f0', marginBottom: 16 }}>
            {editingCategory ? '✏️ Editar Categoría' : 'Nueva Categoría'}
          </h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="🍕"
                className="input"
                style={{ width: 60, textAlign: 'center' }}
                value={form.icon}
                onChange={e => setForm({ ...form, icon: e.target.value })}
                maxLength={4}
              />
              <input
                type="text"
                placeholder="Nombre de la categoría *"
                className="input"
                style={{ flex: 1 }}
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <textarea
              placeholder="Descripción (opcional)"
              className="input"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              style={{ minHeight: 60 }}
            />
            <button type="submit" className="btn btn-success">
              {editingCategory ? '💾 Guardar Cambios' : '+ Crear Categoría'}
            </button>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {categories.map(cat => (
          <div
            key={cat.id}
            className="card"
            draggable
            onDragStart={() => setDraggedId(cat.id)}
            onDragOver={e => e.preventDefault()}
            onDrop={() => handleDrop(cat)}
            style={{ cursor: 'grab', opacity: draggedId === cat.id ? 0.4 : 1 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 28 }}>{cat.icon}</span>
              <div>
                <h3 style={{ color: '#e2e8f0', fontSize: 16 }}>{cat.name}</h3>
                <p style={{ color: '#64748b', fontSize: 12 }}>{cat.productCount} producto{cat.productCount !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {cat.description && <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 12 }}>{cat.description}</p>}
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => startEdit(cat)} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✏️ Editar</button>
              <button onClick={() => deleteCategory(cat)} style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: 'none', background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>🗑️ Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <p style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No hay categorías todavía.</p>
      )}
    </div>
  );
};

export default CategoriesPage;